import { LoyaltyChallengeConfig } from '@shared/models';

import {
  DISPLAY_CONDITION,
  ProductCheckTabType,
  RewardFormat,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const HYDRA_CONFIG: LoyaltyChallengeConfig = {
  currency: 'USD',
  appIcon: 'assets/images/hydra/favicon.ico',
  cssVariables: {
    'font-family-regular': 'Roboto-Regular',
    'font-family-medium': 'Roboto-Medium',
    'font-family-semi-bold': 'Roboto-Semi-Bold',
    'font-family-bold': 'Roboto-Bold',
    'font-family-roboto-slab-medium': 'RobotoSlab-Medium',
    'font-family-roboto-slab-bold': 'RobotoSlab-Bold',
    'font-family-roboto-slab-black': 'RobotoSlab-Black',
    'font-family-roboto-slab-extrabold': 'RobotoSlab-ExtraBold',

    'navbar-height': '75px',
    'navbar-background-color': '#ffffff',

    'nav-link-color': '#75787B',
    'nav-link-hover-color': '#272F35',
    'nav-link-active-color': '#476C73',

    'nav-item-bg-color': 'white',
    'nav-item-active-bg-color': 'white',
    'nav-item-font-family': 'var(--font-family-roboto-slab-extrabold)',
    'nav-item-hover-bg-color': 'rgb(0 0 0 / 10%)',

    'body-color': '#000000',
    'body-background-color': '#ffffff',

    'challenge-primary-color': '#C8102E',

    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',
  },
  rewardFormat: RewardFormat.POINTS,
  og: {
    title: 'Current Challenges | Loyalty Challenge',
    description:
      "Discover the current challenges and earn up to 200 visits per month when you buy your favorite brands. Don't miss the chance to win big!",
  },
  rewardTemplate: '{points}',
  pages: {
    availablePages: ['help', 'current'],
    help: {
      meta: {
        title: 'Current Challenges | Loyalty Challenge | Help Center',
        keywords:
          'loyalty, challenge, current, challenges, loyalty challenge, loyalty challenge current challenges',
        description:
          "Discover the current challenges and earn up to 200 visits per month when you buy your favorite brands. Don't miss the chance to win big!",
      },
      header: {
        background: '#476C73',
        goBackButton: {
          text: 'Go Back To My Challenges',
          backgroundColor: '#C8102E',
          color: 'white',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          borderRadius: '5px',
        },
        bannerAlternativeText: '',
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
          title: {
            color: 'white',
            text: '',
            fontFamily: 'var(--font-family-bold)',
          },
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          label: 'Frequently Asked Questions',
          type: TitleType.TEXT,
          styles: {
            color: '#272f35',
            fontSize: '25px',
            fontFamily: 'var(--font-family-roboto-slab-extrabold)',
          },
        },
        questions: [
          {
            title: 'When will my visits be available on my Kwik Rewards card?',
            content:
              'Visits are added within 3 days after the last day of the month for the monthly challenges. You can view your visits balance in the challenges tab online or in the Kwik Rewards app.',
          },
          {
            title: 'Why did my visits convert to fuel discounts within my app?',
            content:
              'If your visit reward is set to fuel discounts, your visits will be converted to a fuel discount after an instore purchase.',
          },
          {
            title: 'How many challenges can I activate at one time?',
            content: 'You can activate up to 5 challenges each month.',
          },
          {
            title: 'How do I earn visits?',
            content:
              'Once you activate a challenge, you can make qualifying purchases in store or through the app using your Kwik Rewards card.  You’ll earn visits based on your progress, even if the challenge is not fully completed.',
          },
          {
            title:
              'If I spend more than a challenge requires, can I increase my visits earned amount?',
            content:
              'No, each challenge has a set maximum visit reward, so spending more than required won’t increase your visits earned amount.',
          },
          {
            title: 'Which purchases will count toward my activated challenges?',
            content:
              'Qualifying purchases count when you scan your Kwik Rewards card in-store or are signed into your Kwik Rewards account when placing orders online. Purchases of eligible products included in an activated challenge will count toward your earnings progress. Qualifying purchase amounts are calculated before taxes and fees and after any applicable item discounts.',
          },
          {
            title: 'How can I follow the progress of my challenges?',
            content:
              'Your challenge progress will update within a day after an in-store or online purchase. Each challenge has its own progress bar, so you can track your progress through each challenge.',
          },
          {
            title: 'How do I use the visits I earn through the challenges?',
            content:
              'You can redeem your visits on qualifying in-store purchases when you scan your Kwik Rewards card at checkout, or online when you’re signed in to your Kwik Rewards account. Visits will be applied automatically to your next qualifying purchase.',
          },
          {
            title: 'When will my purchases display in the tracker?',
            content:
              'Purchases are added within 5 hours of when the purchase was made instore or online.',
          },
        ],
        accordion: {
          styles: {
            title: {
              fontSize: '16px',
              fontFamily: 'var(--font-family-roboto-slab-medium)',
              color: '#000000',
              margin: '10px 0',
            },
            description: {
              fontSize: '14px',
              fontFamily: 'var(--font-family-regular)',
              color: '#000000',
              margin: '10px 0',
              paddingLeft: '15px',
            },
          },
          iconMore: 'assets/images/hydra/expand-more.svg',
          iconLess: 'assets/images/hydra/expand-less.svg',
          alternativeTextExpandMore: 'Expand More',
          alternativeTextExpandLess: 'Expand Less',
        },
      },
      extraLinks: [
        {
          hideInIframe: true,
          title: {
            text: 'Need further assistance?',
            fontFamily: 'var(--font-family-roboto-slab-bold)',
          },
          link: {
            color: 'white',
            label: 'Contact us',
            fontFamily: 'var(--font-family-roboto-slab-extrabold)',
            backgroundColor: '#C8102E',
            href: 'https://www.kwiktrip.com/contact-us',
          },
        },
      ],
    },
    currentChallenges: {
      meta: {
        title: 'Current Challenges | Loyalty Challenge',
        keywords:
          'loyalty, challenge, current, challenges, loyalty challenge, loyalty challenge current challenges',
        description:
          "Discover the current challenges and earn up to 200 visits per month when you buy your favorite brands. Don't miss the chance to win big!",
      },
      background: 'white',
      howDoesitWork: {
        color: 'white',
        fontFamily: 'var(--font-family-roboto-slab-bold)',
        backgroundColor: '#C8102E',
        containerBackgroundColor: 'white',
        text: 'HOW IT WORKS',
        borderRadius: '6px',
        containerPadding: '2rem 0 0',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'How it works',
            color: '#000000',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: 'var(--challenge-primary-color)',
          },
          skipNowButton: {
            text: 'Skip for now',
            color: 'white',
            fontFamily: 'var(--font-family-roboto-slab-bold)',
            backgroundColor: '#C8102E',
            borderRadius: '6px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
    },
    sorry: {
      background: '#476C73',
      content: [
        '<div style="text-align: center;"><img src="assets/images/hydra/sorry-logo.webp" alt=""></div>',
        '<div style="font-size: 1.5rem; text-align: center; color: white; font-family: var(--font-family-roboto-slab-bold);">Sorry...</div>',
        '<div style="font-size: 1.2rem; text-align: center; color: white; font-family: var(--font-family-roboto-slab-bold);">This site is temporarily unavailable.</div>',
      ],
    },
  },
  components: {
    header: {
      variant: Variants.VARIANT_11,
      backgroundColor: '#658E93',
      bannerAriaLabel:
        'Kwik Rewards challenges banner. Earn up to 200 visits per month.',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/hydra/cover-mobile.webp',
        },
        tablet: {
          imageUrl: 'assets/images/hydra/cover-mobile.webp',
        },
        desktop: {
          imageUrl: 'assets/images/hydra/cover-desktop.webp',
        },
      },
      campaignDate: {
        color: 'white',
        fontFamily: 'var(--font-family-bold)',
        template: 'From {startDate} - {endDate}',
      },
      earnUpToVisits: {
        color: 'white',
        fontFamily: 'var(--font-family-roboto-slab-extrabold)',
        text: 'Earn up to <span class="visits-count">200</span><br/> Visits Per Month',
      },
    },
    brands: {
      variant: Variants.VARIANT_1,
      brandsBgColor: 'white',
      border: '2px solid #00529f',
      alternativeText: '',
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: true,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        backgroundColor: '#476C73',
        borderTop: '1px solid #f4f5f6',
      },
      linkStyles: {
        color: 'white',
        fontSize: '16px',
        textDecoration: 'none',
        fontFamily: 'var(--font-family-bold)',
      },
      links: [
        {
          url: 'help',
          label: 'Help Center',
        },
      ],
    },
    tutorial: {
      background: '#476C73',
      carousel: {
        config: {
          sectionAriaLabel: 'Tutorial carousel',
          controlsAriaLabel: 'Carousel controls',
          paginationAriaLabel: 'Slide indicators',
          title: {
            fontSize: '18px',
            color: 'white',
            text: 'HOW IT WORKS',
            fontFamily: 'var(--font-family-roboto-slab-extrabold)',
          },
          dots: {
            activeBg: '#FFFFFF',
            inactiveBg: '#FFFFFF99',
            ariaLabel: 'Go to slide {variable} of {variable}',
          },
          slideIndicatorLabel: 'Slide {variable} on {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/hydra/chevron-left.svg',
              alternativeText: 'Go to the previous slide',
            },
            rightIcon: {
              imageUrl: 'assets/images/hydra/chevron-right.svg',
              alternativeText: 'Go to the next slide',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_3,
          styles: {
            title: {
              color: '#000000',
              fontFamily: 'var(--font-family-roboto-slab-black)',
            },
            description: {
              color: '#000000',
              fontFamily: 'var(--font-family-regular)',
            },
            card: {
              border: 'none',
              boxShadow: 'none',
              background: 'white',
              borderRadius: '10px',
            },
          },
          steps: [
            {
              title: '1 - ACTIVATE',
              description: 'Select up to {variable} Challenges each month.',
              picture: {
                imageUrl: 'assets/images/hydra/step-1.webp',
                alternativeText:
                  'Select up to {variable} Challenges each month.',
              },
            },
            {
              title: '2 - SHOP',
              description:
                'Scan your Kwik Rewards card in store or log in when shopping online.',
              picture: {
                imageUrl: 'assets/images/hydra/step-2.webp',
                alternativeText:
                  'Scan your Kwik Rewards card in store or log in when shopping online.',
              },
            },
            {
              title: '3 - EARN',
              description:
                'Spend on your favorite brands to earn up to 200 VISITS in Kwik Rewards.',
              picture: {
                imageUrl: 'assets/images/hydra/step-3.webp',
                alternativeText:
                  'Spend on your favorite brands to earn up to 200 VISITS in Kwik Rewards.',
              },
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_5,
      backgroundColor: '#A6192E',
      picture: {
        imageUrl: 'assets/images/hydra/kwik-card.webp',
        alternativeText: 'Kwik Rewards cards',
      },
      earnedRewards: {
        color: 'white',
        text: 'Earned Rewards',
        fontFamily: 'var(--font-family-regular)',
      },
      amount: {
        color: 'white',
        fontFamily: 'var(--font-family-roboto-slab-extrabold)',
      },
      progressText: {
        color: 'white',
        text: 'Visits',
        fontFamily: 'var(--font-family-roboto-slab-extrabold)',
      },
    },
    navbar: {
      variant: Variants.VARIANT_1,
      menuVariant: Variants.VARIANT_4,
      hasCountdownInMenu: true,
      navbarFullWidth: true,
      onlyMarginLeft: true,
      fontFamily: 'var(--font-family-roboto-slab-extrabold)',
      dropdownLabel: 'Menu',
      hasLayoutPaddingTop: true,
      navbarAriaLabel: '',
      openButton: {
        imageAlt: 'Click to see more options',
        buttonTitle: 'Click to see more options',
        imageUrl: 'assets/images/hydra/menu-open-icon.svg',
      },
      closeButton: {
        imageAlt: 'Close icon',
        buttonTitle: 'Close main menu',
        imageUrl: 'assets/images/hydra/menu-close-icon.svg',
      },
      items: [
        {
          url: 'current',
          label: 'My Kwik Rewards Challenges',
          ariaLabel: 'My Kwik Rewards Challenges',
          menuIcon: {
            alternativeText: 'Home Menu Icon',
            imageUrl: 'assets/images/hydra/home.svg',
          },
          iconUrlSelected: {
            alternativeText: 'Home Menu Icon Selected',
            imageUrl: 'assets/images/hydra/home-selected.svg',
          },
        },
        {
          url: 'help',
          label: 'Need help?',
          ariaLabel: 'Need help?',
          menuIcon: {
            alternativeText: 'Help Menu Icon',
            imageUrl: 'assets/images/hydra/help.svg',
          },
          iconUrlSelected: {
            alternativeText: 'Help Menu Icon Selected',
            imageUrl: 'assets/images/hydra/help-selected.svg',
          },
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_1,
      showOnNavbarWhen: [],
      showAfterCampaignSummaryWhen: [],
      showBeforeCampaignSummaryWhen: [],
      showOnPages: 'all',
      titleAppearing: ['column', 'column', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#272F35',
        fontFamily: 'var(--font-family-semi-bold)',
      },
      title: {
        color: '#272F35',
        showTitleOnMobile: true,
        fontFamily: 'var(--font-family-semi-bold)',
        label: 'Challenge ends in',
      },
      numberStyle: {
        color: 'white',
        backgroundColor: '#D82117',
        fontFamily: 'var(--font-family-roboto-slab-extrabold)',
      },
      texts: {
        days: 'days',
        hours: 'hours',
        minutes: 'minutes',
        seconds: 'seconds',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_8,
          backgroundColor: '#476C73',
          backgroundImage: 'assets/images/hydra/win-card.webp',
          completedMsg: {
            color: 'white',
            fontFamily: 'var(--font-family-roboto-slab-extrabold)',
            text: 'Challenge\n Completed',
          },
          rewardMsg: {
            fontFamily: 'var(--font-family-medium)',
            color: '#000000',
            text: "You've earned",
          },
          rewardAmount: {
            color: '#A6192E',
            fontFamily: 'var(--font-family-roboto-slab-black)',
          },
          progressText: {
            color: '#C8102E',
            fontFamily: 'var(--font-family-roboto-slab-black)',
            text: 'Visits',
          },
        },
        selectionButton: {
          variant: Variants.VARIANT_6,
          button: {
            color: 'white',
            label: 'Select This Challenge',
            backgroundColor: '#C8102E',
            title: 'Select This Challenge',
            fontFamily: 'var(--font-family-roboto-slab-bold)',
            textTransform: 'uppercase',
          },
          label: {
            color: '#333333',
            text: 'Earn up to',
            fontFamily: 'var(--font-family-medium)',
          },
          visitsContainer: {
            textColor: '#FFFFFF',
            backgroundColor: '#476C73',
            fontFamily: 'var(--font-family-roboto-slab-black)',
            text: 'Visits',
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_1,
          icon: {
            imageUrl: 'assets/images/hydra/cancel-icon.svg',
            alternativeText: 'Cancel this challenge',
          },
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgba(127, 169, 174, 0.65)',
          borderRadius: '12px',
          banner: {
            border: 'none',
            textColor: 'white',
            borderRadius: '4px',
            backgroundColor: '#A6192E',
            text: 'Challenge Full!',
            fontFamily: 'var(--font-family-roboto-slab-extrabold)',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_3,
          bannerAlternativeText: 'Challenges Banner',
          logoAlternativeText: 'Logo banner',
          selectionButtonbackgroundColor: 'white',
          header: {
            height: '143px',
            backgroundColor: '#FFFFFF',
          },
          subHeader: {
            useProductLabel: true,
            hasInfoButton: false,
            showUnselectionButtonInSubHeader: true,
            color: '#242729',
            completedBackgroundColor: '#476C73',
            backgroundColor: 'white',
            fontFamily: 'var(--font-family-semi-bold)',
            fontSize: '12px',
          },
          body: {
            progressbar: {
              earnLabel: 'Rewards',
              spendLabel: 'Spend',
              labelColor: '#242729',
              progressBarLeft: '48px',
              progressColor: '#C8102E',
              containerColor: '#F2F2F2',
              labelFontFamily: 'var(--font-family-bold)',
              pictureStageReached: {
                imageUrl: 'assets/images/hydra/picture-reached.svg',
                alternativeText: 'Step reached: Marking this step as completed',
              },
            },
            rewards: {
              rewardClass: 'reward-baseline-layout',
              activeLabel: 'Earn',
              passedLabel: 'Earned',
              rewardUnitText: 'Visits',
              flexDirection: 'column-reverse',
              labelFontFamily: 'var(--font-family-bold)',
              color: '',
              fontSize: '16px',
              fontFamily: 'var(--font-family-roboto-slab-black)',
              backgroundColors: {
                bubbleWidth: '75px',
                bubbleHeight: '49px',
                reached: {
                  fill: '#A6192E',
                  stroke: '#A6192E',
                  textColor: 'white',
                  labelColor: 'white',
                  rewardUnitTextColor: 'white',
                },
                unreached: {
                  fill: 'white',
                  stroke: '#000000',
                  textColor: '#000000',
                  labelColor: '#000000',
                  rewardUnitTextColor: '#000000',
                },
                default: {
                  fill: '#A6192E66',
                  stroke: '#A6192E66',
                  textColor: 'white',
                  labelColor: 'white',
                  rewardUnitTextColor: 'white',
                },
                backgroundImage: 'assets/images/hydra/max-reward.svg',
                alternativeText: {
                  reached: 'I earned {variable} visits',
                  default: 'I earned {variable} visits',
                  unreached: 'Earn {variable} visits',
                },
              },
            },
            targets: {
              color: '#242729',
              fontFamily: 'var(--font-family-bold)',
              textDecorationOnReached: 'line-through',
            },
          },
          footer: {
            color: '#000000',
            label: 'Total Spend',
            fontFamily: 'var(--font-family-medium)',
            icon: {
              imageUrl: 'assets/images/hydra/shop.svg',
              alternativeText: 'Cart icon',
            },
            spentAmountFontFamily: 'var(--font-family-bold)',
            productCheckButton: {
              ariaLabel: 'Click to check eligible products',
              color: '#476C73',
              label: 'Eligible Products',
              fontFamily: 'var(--font-family-medium)',
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_1,
          boxShadow: 'rgba(38, 38, 38, 0.08) 0px 4px 24px',
          header: {
            backgroundImage: {
              url: 'assets/images/default/frequency-banner.webp',
              bannerAriaLabel: 'Frequency Challenge',
            },
            height: '80px',
          },
          subHeader: {
            borderBottom: '1px solid #f4f5f6',
            completedBackgroundColor: '#476C73',
            backgroundColor: 'white',
            title: {
              color: 'black',
              label: 'Visits challenge',
              fontFamily: 'var(--font-family)',
            },
          },
          body: {
            label: 'Minimum amount of {variable}€ per visit',
            labelFontFamily: 'var(--font-family-bold)',
            labelFontSize: '14px',
            stamps: {
              showNumbers: false,
              iconSelected: 'assets/images/default/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/default/frequency-stamp-empty.svg',
            },
          },
          footer: {
            color: 'black',
            label: 'Visits: ',
            fontFamily: 'var(--font-family)',
            footerIcon: {
              imageUrl: 'assets/images/default/shop.svg',
              alternativeText: '',
            },
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_3,
      title: {
        color: '#000000',
        text: 'My Kwik Rewards Challenges',
        fontFamily: 'var(--font-family-roboto-slab-extrabold)',
      },
      update: {
        color: '#000000',
        text: 'Last update: {variable}',
        fontFamily: 'var(--font-family-bold)',
        dateFormat: 'MM/dd/yy',
      },
      borderRadius: '4px',
      border: '1px solid #DEDEDE',
      challengeLabels: {
        onGoingLabel: {
          color: '#19191A',
          backgroundColor: 'white',
          text: 'Activated',
          fontFamily: 'var(--font-family-medium)',
        },
        completedLabel: {
          color: '#19191A',
          backgroundColor: 'white',
          text: 'Completed',
          fontFamily: 'var(--font-family-medium)',
        },
      },
      challengesInfos: {
        fontFamily: 'var(--font-family-roboto-slab-black)',
        color: '#C8102E',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '4px',
        backgroundColor: '#C8102E',
        fontFamily: 'var(--font-family-roboto-slab-bold)',
        ariaLabel: 'Close',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          backgroundColor: '',
          backgroundImage: '',
        },
        frequencyHeaderImages: {
          [SubType.DEFAULT]: {
            imageUrl: 'assets/images/default/frequency-header.webp',
            alternativeText: '',
          },
        },
        title: {
          color: '#333333',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          label: 'Are you sure you want to remove this Challenge? ',
        },
        body: {
          color: '#333333',
          fontFamily: 'var(--font-family-medium)',
          label: 'If you change your mind, you can always select it again.',
        },

        actionButtons: {
          variant: Variants.VARIANT_2,
          confirmButton: {
            label: 'Yes',
            borderRadius: '4px',
            textColor: '#FFFFFF',
            backgroundColor: '#C8102E',
            fontFamily: 'var(--font-family-roboto-slab-bold)',
            ariaLabel: 'Yes',
          },
          closeButton: {
            label: 'No',
            borderRadius: '4px',
            textColor: '#C8102E',
            backgroundColor: 'white',
            borderColor: '#C8102E',
            fontFamily: 'var(--font-family-roboto-slab-bold)',
            ariaLabel: 'No',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family-regular)',
        tabs: [
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            label: 'Search',
            searchable: false,
            inputPlaceholder: 'Enter a barcode to search',
            tableTitle: {
              text: 'List of eligible products',
              color: 'var(--challenge-primary-color)',
            },
            columns: {
              name: {
                header: 'Product',
                color: '#000000',
                fontFamily: 'var(--font-family-medium)',
              },
              code: {
                header: 'Barcode',
                color: '#000000',
                fontFamily: 'var(--font-family-medium)',
              },
            },
          },
        ],
      },
      firstSelection: {
        backgroundColor: 'white',
        title: {
          color: '#333333',
          label: 'Challenge selected',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/hydra/first-selection.svg',
            alternativeText: 'You have selected the first challenge',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label: 'You can select up to {variable} more Challenges.',
        },
      },
      lastSelection: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          label: 'All challenges set!',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/hydra/last-selection.svg',
            alternativeText:
              'Shop your favorite brand to win up to 200 VISITS!',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label: 'Shop your favorite brand to win up to 200 VISITS!',
        },
      },
      maximumSelection: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          label: "You've already selected your challenges",
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/hydra/maximum-selection.svg',
            alternativeText:
              'Shop your favorite brand to win up to 200 VISITS!',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            "If you want to make a change, deselect any Challenge you haven't started yet and choose a new one.",
        },
      },
      outOfStockIssue: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          label: 'Limit reached',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'This Challenge has reached the maximum number of participants. Please choose another.',
        },
      },
      genericError: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          label: 'Error',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            "An error occurred, and the Challenge couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          label: 'Request timed out',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'Selecting the Challenge took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-roboto-slab-bold)',
          label: 'Campaign ended',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'This campaign has ended and Challenges are no longer available for selection.',
        },
      },
    },
  },
};
